import { fallbackChannelSlug, normalizeSlug } from './channel_slug.helpers';

/**
 * Parser for Connect search input. Pure (no database access): the server
 * applies it and the client renders the same filters as chips.
 *
 *   design review in:#launch from:@raj
 *   in:"#product launch"  from:"Raj Kumar"
 *
 * `in:` names a channel by slug or display name (`#` optional), `from:` a
 * person by name or email (`@` optional). Quote a value to include spaces. The last filter of a
 * kind wins; everything else is the search text.
 */
export type ConnectSearchQuery = {
  text: string;
  channelSlug: string | null;
  /** The `in:` value as typed, minus `#`; tells hash-suffixed slugs apart. */
  channelName: string | null;
  fromName: string | null;
};

export const CONNECT_SEARCH_MAX_LENGTH = 200;

const FILTER = /(^|\s)(in|from):(?:"([^"]*)"|(\S*))/gi;

/**
 * The slug channel creation gives `value` (`#` optional): the plain Latin slug,
 * or the `channel-<hash>` fallback for names with no [a-z0-9] characters
 * (Japanese, Arabic, emoji…). '' for an empty value.
 */
export function normalizeChannelSlug(value: string): string {
  const name = stripChannelPrefix(value);
  if (!name) return '';
  return normalizeSlug(name) || fallbackChannelSlug(name);
}

/** True when `value` has no Latin slug and so maps to a fallback slug. */
export function usesFallbackChannelSlug(value: string): boolean {
  const name = stripChannelPrefix(value);
  return name.length > 0 && normalizeSlug(name).length === 0;
}

/**
 * The `in:` filter a channel's search button pre-fills. Latin names use the
 * slug; other names use the display name (quoted when it has spaces), which
 * the server matches by name, so a hash-suffixed slug still resolves.
 */
export function channelSearchFilter(channelName: string): string {
  const name = channelName.trim();
  if (!name) return '';
  if (!usesFallbackChannelSlug(name)) return `in:#${normalizeSlug(name)}`;
  const value = `#${name.replace(/"/g, '')}`;
  return /\s/.test(value) ? `in:"${value}"` : `in:${value}`;
}

function stripChannelPrefix(value: string): string {
  return value.trim().replace(/^#/, '').trim();
}

export function parseConnectSearchQuery(raw: string): ConnectSearchQuery {
  let channelSlug: string | null = null;
  let channelName: string | null = null;
  let fromName: string | null = null;
  const text = raw.replace(
    FILTER,
    (_match, lead: string, key: string, quoted?: string, bare?: string) => {
      const value = (quoted ?? bare ?? '').trim();
      if (key.toLowerCase() === 'in') {
        const slug = normalizeChannelSlug(value);
        channelSlug = slug || null;
        channelName = slug ? stripChannelPrefix(value) : null;
      } else {
        const name = value.replace(/^@/, '').trim();
        fromName = name || null;
      }
      return lead;
    }
  );
  return {
    text: text.replace(/\s+/g, ' ').trim(),
    channelSlug,
    channelName,
    fromName,
  };
}
